import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ClipboardList, Loader2, Lock, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getErrorMessage } from "@/lib/errors";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dmy = (d) => (d ? d.split("-").reverse().join("-") : "");
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const CARD = "rounded-2xl border border-border/70 bg-card shadow-sm";

function Notice({ tone, icon: Icon, title, children }) {
  const tones = {
    ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200",
    warn: "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200",
    off: "border-border bg-muted text-foreground",
  };
  return (
    <div className={`flex gap-3 rounded-2xl border p-4 ${tones[tone]}`} role="status">
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0 text-sm"><p className="font-semibold">{title}</p>{children && <p className="mt-0.5 opacity-90">{children}</p>}</div>
    </div>
  );
}

export default function VendorQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rates, setRates] = useState({});
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");
  const [justSent, setJustSent] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const apply = (d) => {
    setData(d);
    setRates(Object.fromEntries(d.items.map((it) => [it.id, d.rates?.[it.id] ?? ""])));
    setRemarks(d.remarks || "");
  };

  useEffect(() => {
    api.get(`/public/rfq/${token}`, { silent: true })
      .then(({ data: d }) => apply(d))
      .catch((err) => setError(err?.response?.status === 404 ? "This link is not valid. Please ask for a fresh link." : getErrorMessage(err)));
  }, [token]);

  const lines = useMemo(() => (data ? data.items.map((it) => {
    const rate = Number(rates[it.id]);
    return { ...it, rate: rate > 0 ? rate : 0, amount: rate > 0 ? rate * Number(it.quantity) : 0 };
  }) : []), [data, rates]);
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const missing = lines.filter((l) => !(l.rate > 0)).length;

  const submit = async (e) => {
    e.preventDefault();
    if (!confirming) { setConfirming(true); return; }
    setSaving(true); setProblem("");
    try {
      const { data: d } = await api.post(`/public/rfq/${token}/quote`, { line_rates: rates, remarks: remarks || null }, { silent: true });
      apply(d); setJustSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setProblem(getErrorMessage(err)); setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return <Shell><Notice tone="warn" icon={AlertTriangle} title="We could not open this request">{error}</Notice></Shell>;
  }
  if (!data) {
    return <Shell><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></Shell>;
  }

  const editable = data.editable;
  const stateNote = {
    SUBMITTED: null,
    NOT_SENT: ["warn", AlertTriangle, "This request is not open yet", "Please check back once you receive the final link."],
    EXPIRED: ["warn", AlertTriangle, "The deadline for this quotation has passed", "If you still want to quote, please contact the buyer."],
    CLOSED: ["off", Lock, "This request is closed", "A decision has been made, so quotes can no longer be changed. Thank you for taking part."],
  }[data.state];

  return (
    <Shell>
      <header className={`${CARD} p-5`}>
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"><ClipboardList className="h-4 w-4" /> Request for quotation</p>
        <h1 className="mt-2 text-xl font-semibold text-foreground sm:text-2xl" data-testid="vq-company">{data.company}</h1>
        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
          <div><dt className="text-xs text-muted-foreground">RFQ number</dt><dd className="font-mono font-medium text-foreground" data-testid="vq-number">{data.rfq.number}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Date</dt><dd className="font-medium text-foreground">{dmy(data.rfq.date)}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Reply by</dt><dd className="font-medium text-foreground">{data.rfq.due_date ? dmy(data.rfq.due_date) : "—"}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Quote for</dt><dd className="font-medium text-foreground" data-testid="vq-vendor">{data.vendor}</dd></div>
        </dl>
        {data.rfq.remarks && <p className="mt-4 whitespace-pre-line border-t border-border/70 pt-3 text-sm text-muted-foreground">{data.rfq.remarks}</p>}
      </header>

      {justSent && <Notice tone="ok" icon={CheckCircle2} title="Thank you, your quote has been sent">{`Total ${inr(data.total)}. Your quote is final and cannot be changed here. If something is wrong, please contact the buyer.`}</Notice>}
      {!justSent && data.state === "SUBMITTED" && <Notice tone="ok" icon={CheckCircle2} title="Your quote has been sent">{`Sent on ${when(data.submitted_at)}. It cannot be changed here. If something is wrong, please contact the buyer.`}</Notice>}
      {stateNote && <Notice tone={stateNote[0]} icon={stateNote[1]} title={stateNote[2]}>{stateNote[3]}</Notice>}

      <form onSubmit={submit} className={`${CARD} overflow-hidden`}>
        <div className="border-b border-border/70 px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">{editable ? "Enter your price for each item" : data.state === "SUBMITTED" ? "Your quote" : "Items requested"}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{editable ? "Price per unit, excluding GST. The amount is worked out for you. Once sent, the quote cannot be changed." : "Price per unit, excluding GST."}</p>
        </div>
        <ul className="divide-y divide-border/60" data-testid="vq-items">
          {lines.map((l, i) => (
            <li key={l.id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-4" data-testid={`vq-item-${i}`}>
              <div className="min-w-0 flex-1 basis-56">
                <p className="text-sm font-medium text-foreground"><span className="mr-2 text-muted-foreground">{i + 1}.</span>{l.description}</p>
                <p className="mt-1 text-xs text-muted-foreground">Quantity <span className="font-mono font-medium text-foreground">{Number(l.quantity).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</span>{l.unit ? ` ${l.unit}` : ""}</p>
              </div>
              <div className="w-full sm:w-52">
                <Label className="sr-only" htmlFor={`rate-${l.id}`}>Price per unit for {l.description}</Label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">₹</span>
                  <Input id={`rate-${l.id}`} inputMode="decimal" type="number" min="0" step="0.01" disabled={!editable} value={rates[l.id] ?? ""}
                    onChange={(e) => { setConfirming(false); setRates({ ...rates, [l.id]: e.target.value }); }} placeholder="Price per unit"
                    className="pl-7 text-right font-mono" data-testid={`vq-rate-${i}`} />
                </div>
                <p className="mt-1 text-right text-xs text-muted-foreground">{l.amount > 0 ? <>Amount <span className="font-mono font-medium text-foreground">{inr(l.amount)}</span></> : " "}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-3 border-t border-border/70 bg-muted/40 px-5 py-4">
          <span className="text-sm font-medium text-foreground">Total (excluding GST)</span>
          <span className="font-mono text-lg font-semibold text-foreground" data-testid="vq-total">{inr(total)}</span>
        </div>
        {editable && (
          <div className="space-y-4 border-t border-border/70 px-5 py-4">
            <div>
              <Label htmlFor="vq-remarks">Notes <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Textarea id="vq-remarks" rows={3} className="mt-1.5" value={remarks} maxLength={1000} onChange={(e) => setRemarks(e.target.value)}
                placeholder="Delivery time, GST rate, payment terms, how long the prices are valid…" data-testid="vq-remarks" />
            </div>
            {problem && <p className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300" role="alert" data-testid="vq-problem">{problem}</p>}
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={saving || missing > 0} data-testid="vq-submit">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {confirming ? "Yes, send final quote" : "Send quote"}
              </Button>
              {confirming && <Button type="button" variant="outline" size="lg" onClick={() => setConfirming(false)} data-testid="vq-back">Go back and check</Button>}
              {confirming && <p className="w-full text-sm text-amber-700 dark:text-amber-300" data-testid="vq-confirm-note">Total {inr(total)}. Once sent, you cannot change this quote.</p>}
              {missing > 0 && <span className="text-xs text-muted-foreground">{missing} item{missing > 1 ? "s" : ""} still {missing > 1 ? "need" : "needs"} a price</span>}
            </div>
          </div>
        )}
      </form>
      <p className="pb-6 text-center text-xs text-muted-foreground">This is a private link for {data.vendor}. Please do not share it.</p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-muted/30 px-4 py-6 sm:py-10">
      <div className="mx-auto max-w-3xl space-y-5">{children}</div>
    </div>
  );
}
